/**
 * PostHog Analytics Client
 * Singleton wrapper with environment-aware initialization,
 * PII sanitization, and typed helpers.
 */

import posthog from 'posthog-js'
import type { CaptureResult } from 'posthog-js'
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'

// Properties that must NEVER be sent to PostHog
const PII_FIELDS = ['password', 'token', 'secret', 'credit_card', 'ssn']

// Local hostnames where PostHog should be disabled
const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1']
const LOCAL_HOSTNAME_PATTERNS = ['tailda419c.ts.net']

let initialized = false

/**
 * Check if the current environment is local (dev machine).
 * PostHog should be completely disabled on local environments.
 *
 * Native Capacitor builds load from `capacitor://localhost/` (or a custom
 * scheme like `ZRM://localhost/`), so `window.location.hostname` is
 * `'localhost'` on every iOS/Android install. Without the explicit
 * Capacitor check, native apps would be treated as local dev and every
 * event dropped before init.
 */
export function isLocalEnvironment(): boolean {
  if (typeof window === 'undefined') return true

  if (Capacitor.isNativePlatform()) return false

  const hostname = window.location.hostname

  if (LOCAL_HOSTNAMES.includes(hostname)) return true
  if (LOCAL_HOSTNAME_PATTERNS.some(pattern => hostname.includes(pattern))) return true

  return false
}

/**
 * Check if PostHog should be active in this environment.
 * Requires a valid API key AND a non-local hostname.
 */
export function isPostHogEnabled(): boolean {
  const key = import.meta.env.VITE_POSTHOG_KEY
  if (!key) return false
  if (isLocalEnvironment()) return false
  return true
}

/**
 * Rewrite native Capacitor URLs (`capacitor://localhost/...` or
 * `<scheme>://localhost/...`) into a meaningful form for product analytics.
 * posthog-js is the web SDK running inside WKWebView, so without this every
 * native event reports `$current_url` as a useless capacitor:// URL and
 * `$host` as `localhost`.
 *
 * Runs via `before_send` so it covers autocapture, pageviews, exceptions —
 * everything that attaches `$current_url` automatically.
 */
export function beforeSendEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event || !event.properties) return event
  if (!Capacitor.isNativePlatform()) return event

  const platform = Capacitor.getPlatform()
  const rawUrl = event.properties.$current_url
  if (typeof rawUrl === 'string') {
    try {
      const u = new URL(rawUrl)
      event.properties.$current_url = `app://${platform}${u.pathname}${u.search}${u.hash}`
      event.properties.$host = `${platform}.app`
      event.properties.$pathname = u.pathname
    } catch {
      // Malformed URL; leave original in place
    }
  }
  return event
}

/**
 * Strip PII fields from event properties before they're sent.
 *
 * The exact-match key `token` is PostHog's own project-token field that the
 * SDK attaches to every outbound event (posthog-core sets
 * `properties.token = config.token`). Removing it causes PostHog's
 * ingestion endpoint to reject the request with
 * "event submitted without an api_key" (401), silently dropping all
 * telemetry. Substring matches like `auth_token` / `api_token` are still
 * scrubbed because user-level credentials shouldn't leave the client.
 */
export function sanitizeProperties(
  properties: Record<string, unknown>,
  _eventName?: string
): Record<string, unknown> {
  const sanitized = { ...properties }
  for (const key of Object.keys(sanitized)) {
    if (key === 'token') continue
    if (PII_FIELDS.some(pii => key.toLowerCase().includes(pii))) {
      delete sanitized[key]
    }
  }
  return sanitized
}

/**
 * Attach platform/version super-properties so every event carries enough
 * context to segment iOS vs Android vs web, distinguish the Dev app from the
 * prod app, and pin down which OTA bundle produced the event.
 */
export async function registerPlatformContext(): Promise<void> {
  if (!initialized) return

  const platform = Capacitor.getPlatform()
  const native = Capacitor.isNativePlatform()

  const superProps: Record<string, unknown> = {
    app_platform: platform,
    app_native: native,
    app_version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown',
  }

  if (native) {
    try {
      const info = await App.getInfo()
      superProps.app_bundle_id = info.id
      superProps.app_native_version = info.version
      superProps.app_build = info.build
    } catch {
      // App plugin unavailable (e.g. in tests); ignore
    }
  }

  posthog.register(superProps)
}

/**
 * Initialize PostHog. No-op if disabled or already initialized.
 */
export function initPostHog(): void {
  if (initialized || !isPostHogEnabled()) return

  const key = import.meta.env.VITE_POSTHOG_KEY
  const host = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com'

  posthog.init(key, {
    api_host: host,
    autocapture: true,
    capture_pageview: false, // Manual via React Router
    capture_pageleave: true,
    persistence: 'localStorage',
    disable_session_recording: true,
    disable_compression: true,
    person_profiles: 'always',
    mask_all_text: false,
    mask_all_element_attributes: false,
    capture_exceptions: true,
    capture_performance: true,
    sanitize_properties: sanitizeProperties,
    before_send: beforeSendEvent,
    loaded: (ph) => {
      if (import.meta.env.DEV) ph.debug()
    },
  })

  initialized = true
  void registerPlatformContext()
}

/**
 * Capture a typed analytics event.
 */
export function captureEvent(
  event: string,
  properties?: Record<string, unknown>
): void {
  if (!initialized || !isPostHogEnabled()) return
  posthog.capture(event, properties)
}

/**
 * Identify a user with person properties.
 */
export function identifyUser(
  userId: string,
  properties?: Record<string, unknown>
): void {
  if (!initialized || !isPostHogEnabled()) return
  posthog.identify(userId, properties)
}

/**
 * Reset PostHog identity (on sign-out).
 */
export function resetUser(): void {
  if (!initialized || !isPostHogEnabled()) return
  posthog.reset()
}

/**
 * Manually capture an exception for error tracking.
 *
 * Delegates to posthog-js's `captureException`, which normalizes the error
 * into the structured `$exception_list` schema that PostHog Error Tracking
 * indexes on. Building `$exception` events via `posthog.capture()` with flat
 * `$exception_message`/`$exception_type`/`$exception_stack_trace_raw`
 * properties produces events that Error Tracking silently drops.
 */
export function captureException(
  error: Error,
  additionalProperties?: Record<string, unknown>
): void {
  if (!initialized || !isPostHogEnabled()) return
  posthog.captureException(error, additionalProperties)
}

/**
 * Get the value of a feature flag.
 */
export function getFeatureFlag(
  key: string
): boolean | string | undefined {
  if (!initialized || !isPostHogEnabled()) return undefined
  return posthog.getFeatureFlag(key)
}

/**
 * Check if a feature flag is enabled (boolean shortcut).
 */
export function isFeatureFlagEnabled(key: string): boolean {
  if (!initialized || !isPostHogEnabled()) return false
  return posthog.isFeatureEnabled(key) ?? false
}

/**
 * Get the raw PostHog instance, or null if not initialized.
 */
export function getPostHogInstance(): typeof posthog | null {
  if (!initialized || !isPostHogEnabled()) return null
  return posthog
}

/**
 * Shutdown PostHog (cleanup on unmount).
 * Resets identity and marks as uninitialized.
 */
export function shutdownPostHog(): void {
  if (!initialized) return
  posthog.reset()
  initialized = false
}
